/**
 * Analytics dashboard — daily KPIs and per-shop breakdown from Snowflake.
 * Features: date range picker, shop filter, KPI scorecards, data tables.
 */
import { Button, icons } from '@apuestapr/ui';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import { BetsBySportPage } from './BetsBySportPage.tsx';
import { createAnalyticsApi, type DailyStatsRow, type ShopStatsRow } from './api.ts';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatNumber = (n: number): string => new Intl.NumberFormat('en-US').format(n);

const formatPercent = (n: number): string => {
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
};

const formatDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('es-PR', { day: 'numeric', month: 'short' });
};

/** Get today in YYYY-MM-DD */
const today = (): string => new Date().toISOString().slice(0, 10);

/** Get a date N days ago in YYYY-MM-DD */
const daysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// ─── KPI Card ────────────────────────────────────────────────────────────────

const KpiCard = ({
  label,
  value,
  subtitle,
  color = 'text-foreground',
}: {
  readonly label: string;
  readonly value: string;
  readonly subtitle?: string;
  readonly color?: string;
}): ReactElement => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-raised">
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className={`mt-1 font-display text-2xl font-bold ${color}`}>{value}</p>
    {subtitle !== undefined ? (
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
    ) : null}
  </div>
);

// ─── Date Range Picker ───────────────────────────────────────────────────────

const presets = [
  { label: 'Hoy', from: today(), to: today() },
  { label: '7 dias', from: daysAgo(7), to: today() },
  { label: '30 dias', from: daysAgo(30), to: today() },
  { label: '90 dias', from: daysAgo(90), to: today() },
] as const;

const DateRangePicker = ({
  from,
  to,
  onChange,
}: {
  readonly from: string;
  readonly to: string;
  readonly onChange: (from: string, to: string) => void;
}): ReactElement => (
  <div className="flex flex-wrap items-center gap-2">
    {presets.map((preset) => (
      <button
        key={preset.label}
        type="button"
        onClick={() => onChange(preset.from, preset.to)}
        className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
          from === preset.from && to === preset.to
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
        }`}
      >
        {preset.label}
      </button>
    ))}
    <div className="flex items-center gap-1 ml-2">
      <input
        type="date"
        value={from}
        onChange={(e) => onChange(e.target.value, to)}
        className="rounded-lg border border-border bg-card px-2 py-1 text-sm"
      />
      <span className="text-muted-foreground text-sm">—</span>
      <input
        type="date"
        value={to}
        onChange={(e) => onChange(from, e.target.value)}
        className="rounded-lg border border-border bg-card px-2 py-1 text-sm"
      />
    </div>
  </div>
);

// ─── Shop Filter ─────────────────────────────────────────────────────────────

const ShopFilter = ({
  shops,
  selected,
  onChange,
}: {
  readonly shops: readonly string[];
  readonly selected: string;
  readonly onChange: (shop: string) => void;
}): ReactElement => (
  <select
    value={selected}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
  >
    <option value="">Todas las localidades</option>
    {shops.map((shop) => (
      <option key={shop} value={shop}>
        {shop}
      </option>
    ))}
  </select>
);

// ─── Daily Stats Table ───────────────────────────────────────────────────────

const DailyStatsTable = ({ rows }: { readonly rows: readonly DailyStatsRow[] }): ReactElement => (
  <div className="overflow-x-auto rounded-xl border border-border">
    <table className="w-full text-sm">
      <thead className="bg-muted/50">
        <tr>
          <th className="px-3 py-2 text-left font-medium">Fecha</th>
          <th className="px-3 py-2 text-right font-medium">Apuestas</th>
          <th className="px-3 py-2 text-right font-medium">Jugadores</th>
          <th className="px-3 py-2 text-right font-medium">Handle</th>
          <th className="px-3 py-2 text-right font-medium">Payout</th>
          <th className="px-3 py-2 text-right font-medium">GGR</th>
          <th className="px-3 py-2 text-right font-medium">NGR</th>
          <th className="px-3 py-2 text-right font-medium">Hold %</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((row) => (
          <tr key={row.date} className="hover:bg-muted/30 transition-colors">
            <td className="px-3 py-2 font-medium">{formatDate(row.date)}</td>
            <td className="px-3 py-2 text-right">{formatNumber(row.totalBets)}</td>
            <td className="px-3 py-2 text-right">{formatNumber(row.activePlayers)}</td>
            <td className="px-3 py-2 text-right">{formatCurrency(row.handle)}</td>
            <td className="px-3 py-2 text-right">{formatCurrency(row.payout)}</td>
            <td
              className={`px-3 py-2 text-right font-semibold ${row.grossResult >= 0 ? 'text-foreground' : 'text-destructive'}`}
            >
              {formatCurrency(row.grossResult)}
            </td>
            <td
              className={`px-3 py-2 text-right ${row.netResult >= 0 ? 'text-foreground' : 'text-destructive'}`}
            >
              {formatCurrency(row.netResult)}
            </td>
            <td className="px-3 py-2 text-right">{formatPercent(row.hold)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Shop Stats Table ────────────────────────────────────────────────────────

const ShopStatsTable = ({ rows }: { readonly rows: readonly ShopStatsRow[] }): ReactElement => (
  <div className="overflow-x-auto rounded-xl border border-border">
    <table className="w-full text-sm">
      <thead className="bg-muted/50">
        <tr>
          <th className="px-3 py-2 text-left font-medium">Fecha</th>
          <th className="px-3 py-2 text-left font-medium">Localidad</th>
          <th className="px-3 py-2 text-right font-medium">Apuestas</th>
          <th className="px-3 py-2 text-right font-medium">Handle</th>
          <th className="px-3 py-2 text-right font-medium">Payout</th>
          <th className="px-3 py-2 text-right font-medium">GGR</th>
          <th className="px-3 py-2 text-right font-medium">Jugadores</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((row, i) => (
          <tr
            key={`${row.date}-${row.shop}-${String(i)}`}
            className="hover:bg-muted/30 transition-colors"
          >
            <td className="px-3 py-2 font-medium">{formatDate(row.date)}</td>
            <td className="px-3 py-2">{row.shop}</td>
            <td className="px-3 py-2 text-right">{formatNumber(row.betsPlaced)}</td>
            <td className="px-3 py-2 text-right">{formatCurrency(row.handle)}</td>
            <td className="px-3 py-2 text-right">{formatCurrency(row.payout)}</td>
            <td
              className={`px-3 py-2 text-right font-semibold ${row.grossResult >= 0 ? 'text-foreground' : 'text-destructive'}`}
            >
              {formatCurrency(row.grossResult)}
            </td>
            <td className="px-3 py-2 text-right">{formatNumber(row.players)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

type Tab = 'overview' | 'by-shop' | 'by-sport';

export const AnalyticsPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const api = useMemo(() => createAnalyticsApi(session.client), [session.client]);

  const [tab, setTab] = useState<Tab>('overview');
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(today());
  const [shops, setShops] = useState<readonly string[]>([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [dailyRows, setDailyRows] = useState<readonly DailyStatsRow[]>([]);
  const [shopRows, setShopRows] = useState<readonly ShopStatsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load shop list once
  useEffect(() => {
    void api
      .getShops()
      .then(setShops)
      .catch(() => {});
  }, [api]);

  // Load data when date range or tab changes
  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setLoading(true);
      try {
        if (tab === 'overview') {
          const result = await api.getDailyStats(from, to);
          if (!cancelled) setDailyRows(result.rows);
        } else if (tab === 'by-shop') {
          const result = await api.getShopStats(from, to, selectedShop || undefined);
          if (!cancelled) setShopRows(result.rows);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [api, from, to, tab, selectedShop, refreshKey]);

  const handleDateChange = useCallback((newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
  }, []);

  // Compute KPI aggregates from daily rows
  const kpis = useMemo(() => {
    if (dailyRows.length === 0) return null;
    const totalBets = dailyRows.reduce((s, r) => s + r.totalBets, 0);
    const totalHandle = dailyRows.reduce((s, r) => s + r.handle, 0);
    const totalGGR = dailyRows.reduce((s, r) => s + r.grossResult, 0);
    const totalNGR = dailyRows.reduce((s, r) => s + r.netResult, 0);
    const totalPlayers = dailyRows.reduce((s, r) => s + r.activePlayers, 0);
    const avgPlayers = Math.round(totalPlayers / dailyRows.length);
    const avgHold = totalHandle > 0 ? totalGGR / totalHandle : 0;
    return { totalBets, totalHandle, totalGGR, totalNGR, avgPlayers, avgHold };
  }, [dailyRows]);

  // Shop KPI aggregates
  const shopKpis = useMemo(() => {
    if (shopRows.length === 0) return null;
    const totalBets = shopRows.reduce((s, r) => s + r.betsPlaced, 0);
    const totalHandle = shopRows.reduce((s, r) => s + r.handle, 0);
    const totalGGR = shopRows.reduce((s, r) => s + r.grossResult, 0);
    const uniqueShops = new Set(shopRows.map((r) => r.shop)).size;
    return { totalBets, totalHandle, totalGGR, uniqueShops };
  }, [shopRows]);

  if (!authenticated) return <></>;

  return (
    <section aria-labelledby="analytics-heading" className="space-y-6">
      {/* Header */}
      <div>
        <h1 id="analytics-heading" className="font-display text-2xl font-bold">
          Analytics
        </h1>
        <p className="text-muted-foreground">Metricas diarias de apuestas, jugadores e ingresos.</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-border pb-2">
        <button
          type="button"
          onClick={() => setTab('overview')}
          className={`px-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'overview'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Vista General
        </button>
        <button
          type="button"
          onClick={() => setTab('by-shop')}
          className={`px-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'by-shop'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Por Localidad
        </button>
        <button
          type="button"
          onClick={() => setTab('by-sport')}
          className={`px-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'by-sport'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          Por Deporte
        </button>
      </div>

      {/* Controls */}
      {tab !== 'by-sport' ? (
        <div className="flex flex-wrap items-center gap-4">
          <DateRangePicker from={from} to={to} onChange={handleDateChange} />
          {tab === 'by-shop' ? (
            <ShopFilter shops={shops} selected={selectedShop} onChange={setSelectedShop} />
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={loading}
          >
            <icons.refresh className="h-4 w-4" />
            {loading ? 'Cargando...' : 'Actualizar'}
          </Button>
        </div>
      ) : null}

      {/* Loading */}
      {loading && tab !== 'by-sport' ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-muted border-t-primary" />
        </div>
      ) : null}

      {/* Overview Tab */}
      {tab === 'overview' && !loading ? (
        <div className="space-y-6">
          {/* KPI Cards */}
          {kpis !== null ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <KpiCard label="Total Apuestas" value={formatNumber(kpis.totalBets)} />
              <KpiCard label="Handle" value={formatCurrency(kpis.totalHandle)} />
              <KpiCard
                label="GGR"
                value={formatCurrency(kpis.totalGGR)}
                color={kpis.totalGGR >= 0 ? 'text-foreground' : 'text-destructive'}
              />
              <KpiCard
                label="NGR"
                value={formatCurrency(kpis.totalNGR)}
                color={kpis.totalNGR >= 0 ? 'text-foreground' : 'text-destructive'}
              />
              <KpiCard label="Jugadores/dia" value={formatNumber(kpis.avgPlayers)} />
              <KpiCard label="Hold %" value={formatPercent(kpis.avgHold)} />
            </div>
          ) : null}

          {/* Daily Table */}
          {dailyRows.length > 0 ? (
            <DailyStatsTable rows={dailyRows} />
          ) : !loading ? (
            <p className="text-center text-muted-foreground py-8">
              No hay datos para el rango seleccionado.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* By-Shop Tab */}
      {tab === 'by-shop' && !loading ? (
        <div className="space-y-6">
          {/* Shop KPI Cards */}
          {shopKpis !== null ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard label="Total Apuestas" value={formatNumber(shopKpis.totalBets)} />
              <KpiCard label="Handle" value={formatCurrency(shopKpis.totalHandle)} />
              <KpiCard
                label="GGR"
                value={formatCurrency(shopKpis.totalGGR)}
                color={shopKpis.totalGGR >= 0 ? 'text-foreground' : 'text-destructive'}
              />
              <KpiCard label="Localidades" value={formatNumber(shopKpis.uniqueShops)} />
            </div>
          ) : null}

          {/* Shop Table */}
          {shopRows.length > 0 ? (
            <ShopStatsTable rows={shopRows} />
          ) : !loading ? (
            <p className="text-center text-muted-foreground py-8">
              No hay datos para el rango seleccionado.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* By-Sport Tab */}
      {tab === 'by-sport' ? <BetsBySportPage /> : null}
    </section>
  );
};
